import React, { useState, useEffect } from 'react';
import styles from './PermissionEditModal.module.css';
import { InnocodeButton, InnocodeCheckbox, useDialog, useInnocodeLoadingOverlay } from '@intsoft/innocode-core';
import { IAM_SERVER_SERVICE_PAGEAUTH } from '../../../utils/Config';
import axios from '../../../utils/api/axios';

/**
 * 권한 수정 모달
 * @param {Object} permission - 수정할 권한 정보
 * @param {Function} onClose - 모달 닫기 함수
 * @param {Function} onSave - 저장 후 콜백 함수
 */
const PermissionEditModal = ({ permission, onClose, onSave }) => {
    const { showDialog } = useDialog();
    const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();

    const [formData, setFormData] = useState({
        authorityDetailId: '',
        authorityName: '',
        create: false,
        read: false,
        update: false,
        delete: false
    });

    useEffect(() => {
        if (permission) {
            setFormData({
                authorityDetailId: permission.authorityDetailId || '',
                authorityName: permission.authorityName || '',
                create: permission.create || false,
                read: permission.read || false,
                update: permission.update || false,
                delete: permission.delete || false
            });
        }
    }, [permission]);

    const handleSave = () => {
        showDialog({
            iconType: "info",
            type: 'confirm',
            title: "권한 저장 확인",
            message: "권한을 저장하시겠습니까?",
            confirmText: "예",
            cancelText: "아니요",
            onConfirm: async () => {
                showLoading();
                try {
                    await axios.post(`${IAM_SERVER_SERVICE_PAGEAUTH}/update`, formData);
                    
                    showDialog({
                        iconType: "check",
                        type: 'info',
                        title: "성공",
                        message: "권한이 저장되었습니다.",
                        confirmText: "확인",
                        onConfirm: () => {
                            if (onSave) onSave();
                            onClose();
                        }
                    });
                } catch (err) {
                    showDialog({
                        iconType: "warning",
                        type: 'error',
                        title: "실패",
                        message: err.response?.data?.message || "저장 실패",
                        confirmText: "확인",
                    });
                } finally {
                    hideLoading();
                }
            },
        });
    };

    const handleDelete = () => {
        showDialog({
            iconType: "info",
            type: 'confirm',
            title: "권한 삭제 확인",
            message: "권한을 삭제하시겠습니까?",
            confirmText: "예",
            cancelText: "아니요",
            onConfirm: async () => {
                //harin: 기능 신규 추가(20260206), API 기능 작동 여부 검증 필요.
                showLoading();
                try {
                    const response = await axios.post(`${IAM_SERVER_SERVICE_PAGEAUTH}/delete/${formData.authorityDetailId}`);
                    console.log("response", response);
                    showDialog({
                        iconType: "check",
                        type: 'info',
                        title: "성공",
                        message: "권한이 삭제되었습니다.",
                        confirmText: "확인",
                        onConfirm: () => {
                            onClose();
                        }
                    });
                } catch (err) {
                    showDialog({
                        iconType: "warning",
                        type: 'error',
                        title: "실패",
                        message: err.response?.data?.message || "삭제 실패",
                        confirmText: "확인",
                    });
                } finally {
                    hideLoading();
                }
            },
        });
    };
    return (
        <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>권한 수정</h3>
                <InnocodeButton
              theme={"opacity"}
              designType='assistive'
              enablePadding={false}
              iconOnly={true}
              icon={<svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 17.94 6M18 18 6.06 6" />
              </svg>
              }
              onClick={onClose}
          /> 
            </div>

            <div className={styles.modalContent}>
                <div className={styles.infoSection}>
                    <p className={styles.label}>권한 명</p>
                    <p className={styles.value}>{formData.authorityName}</p>
                </div>

                <div className={styles.permissionSection}>
                    <p className={styles.sectionTitle}>권한 설정</p>
                    <div className={styles.checkboxGroup}>
                        <div className={styles.checkboxItem}>
                            <InnocodeCheckbox
                                leadingIcon={true}
                                title="생성 (Create)"
                                checked={formData.create}
                                onChange={(checked) => setFormData(prev => ({ ...prev, create: checked }))}
                            /> 
                        </div>
                        <div className={styles.checkboxItem}>
                            <InnocodeCheckbox
                                leadingIcon={true}
                                title="읽기 (Read)"
                                checked={formData.read}
                                onChange={(checked) => setFormData(prev => ({ ...prev, read: checked }))}
                            /> 
                        </div>
                        <div className={styles.checkboxItem}>
                            <InnocodeCheckbox
                                leadingIcon={true}
                                title="수정 (Update)"
                                checked={formData.update}
                                onChange={(checked) => setFormData(prev => ({ ...prev, update: checked }))}
                            /> 
                        </div>
                        <div className={styles.checkboxItem}>
                            <InnocodeCheckbox
                                leadingIcon={true}
                                title="삭제 (Delete)"
                                checked={formData.delete}
                                onChange={(checked) => setFormData(prev => ({ ...prev, delete: checked }))}
                            /> 
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.modalFooter}>
                <InnocodeButton
                    size="lg"
                    theme="solid" 
                    backgroundColor='var(--status-negative, #f83a3a)'
                    onClick={handleDelete}
                >
                    삭제
                </InnocodeButton>
                <InnocodeButton
                    size="lg" 
                    designType="primary"
                    onClick={handleSave}
                >
                    저장
                </InnocodeButton>
            </div>
        </div>
    );
};

export default PermissionEditModal;
